// src/components/forms/shared/ProductSearchInput.tsx
// Componente reutilizable para búsqueda de productos
// Usado por: OrderForm y CotizacionForm

import React from 'react'
import type { Product } from '../../../types/orderFormTypes'
import { Input } from '../../ui'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { isProductInactiveInSearch } from '../../../utils/productSearchUtils'

interface ProductSearchInputProps {
  searchTerm: string
  searchResults: Product[]
  isSearching: boolean
  showResults: boolean
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectProduct: (product: Product) => void
  onFocus: () => void
  onBlur: () => void
  placeholder?: string
  disabled?: boolean
}

export const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
  searchTerm,
  searchResults,
  isSearching,
  showResults,
  onSearchChange,
  onSelectProduct,
  onFocus,
  onBlur,
  placeholder = 'Buscar producto por nombre o SKU',
  disabled = false
}) => {
  return (
    <div className="relative w-64">
      {/* Input de búsqueda */}
      <Input
        type="text"
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        isLoading={false}
        leftIcon={MagnifyingGlassIcon}
        className="w-full"
      />
      
      {/* Dropdown de resultados con información detallada */}
      {showResults && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto animate-slide-down">
          {searchResults.map((product, idx) => {
            const inactive = isProductInactiveInSearch(product)
            const key = `${product.sku ?? product.product_id}-${idx}`
            const rowInner = (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{product.sku}</span>
                  {inactive && (
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                      Inactivo
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">{product.name}</div>
                <div className={`text-sm ${inactive ? 'text-slate-500' : 'text-blue-800'}`}>
                  Precio: ${parseFloat(String(product.price ?? 0)).toLocaleString('es-CO')}
                </div>
              </>
            )
            if (inactive) {
              return (
                <div
                  key={key}
                  role="option"
                  aria-selected={false}
                  aria-disabled
                  className="w-full cursor-not-allowed border-b border-gray-100 bg-gray-50/90 p-3 text-left opacity-80 last:border-b-0"
                >
                  {rowInner}
                </div>
              )
            }
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelectProduct(product)}
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
                className="w-full border-b border-gray-100 p-3 text-left transition-colors duration-200 last:border-b-0 hover:bg-blue-50"
              >
                {rowInner}
              </button>
            )
          })}
        </div>
      )}
      
      {/* Mensaje "No se encontraron resultados" */}
      {showResults && searchResults.length === 0 && searchTerm.length >= 4 && !isSearching && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500 animate-slide-down">
          No se encontraron resultados
        </div>
      )}
    </div>
  )
}
